import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useNavigationType } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, locationsAPI, shippingAddressesAPI, Listing } from '../api';
import ListingCard from '../components/ListingCard';
import HomeLocationModal from '../components/HomeLocationModal';
import {
  loadHomeDeliveryLocation,
  formatStoredDeliveryLabel,
  saveHomeDeliveryLocation,
  saveDeliveryLocationFromModal,
  hasUserDeliveryLocation,
} from '../utils/homeLocationStorage';
import { getCurrentLocation, findLocationIds, geocodeLocation } from '../utils/geolocation';
import { isDefaultAddress, parseStreetFromAddress, parseCityFromAddress, parseStateFromAddress } from '../utils/addressUtils';
import { formatListingCategoryLabel, normalizeListingCategory } from '../constants/listingCategories';
import {
  algoliaHitToListing,
  matchesItemType,
  searchAlgoliaListings,
} from '../services/algoliaSearch';
import { filterListingsByDeliveryRange } from '../utils/listingDistanceFilter';
import {
  applyHomeBrowseFilters,
  countHomeBrowseFilters,
  LISTING_CONDITION_OPTIONS,
  LISTING_SELLER_TYPE_OPTIONS,
} from '../utils/homeBrowseFilters';
import HomeBrowseFilterFields, {
  type HomeBrowseFilterDraft,
} from '../components/HomeBrowseFilterFields';
import {
  consumeHomeBrowseState,
  homeCoordsKey,
  homeFiltersKey,
  readHomeListingsCache,
  saveHomeBrowseState,
  saveHomeListingsCache,
} from '../utils/homeScrollRestoration';

const PAGE_SIZE = 24;
const HOME_SKELETON_COUNT = 12;

function HomeListingsSkeleton() {
  return (
    <div
      id="home-listings-grid"
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5"
      aria-busy="true"
      aria-label="Loading listings"
    >
      {Array.from({ length: HOME_SKELETON_COUNT }, (_, i) => (
        <div
          key={i}
          className="bg-white rounded-xl overflow-hidden border border-gray-200 h-full flex flex-col"
        >
          <div className="w-full aspect-[4/3] bg-gray-200 animate-pulse" />
          <div className="p-3 sm:p-4 space-y-2.5 flex-1">
            <div className="h-3.5 bg-gray-200 rounded animate-pulse w-[88%]" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-2/3" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3 mt-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_FILTERS = {
  category: '',
  search: '',
  item_type: '',
};

function formatHomeMobileDeliveryLine(
  userLocation: {
    city?: string;
    state?: string;
    streetAddress?: string;
    streetNumber?: string;
    streetName?: string;
  } | null
): string {
  if (!userLocation) return 'Set delivery address';
  const streetAddress =
    userLocation.streetAddress?.trim() ||
    [userLocation.streetNumber, userLocation.streetName].filter(Boolean).join(' ').trim();
  if (streetAddress) return streetAddress;
  const fromStored = formatStoredDeliveryLabel(loadHomeDeliveryLocation());
  if (fromStored) return fromStored;
  if (userLocation.city?.trim() && userLocation.state?.trim()) {
    return `${userLocation.city.trim()}, ${userLocation.state.trim()}`;
  }
  return 'Delivery address set';
}

const EMPTY_BROWSE_FILTERS = {
  minPrice: '',
  maxPrice: '',
  condition: '',
  sellerType: '',
};

const EMPTY_BROWSE_DRAFT: HomeBrowseFilterDraft = {
  ...EMPTY_BROWSE_FILTERS,
  category: '',
};

function coordsNearlyEqual(
  a: { lat: number; lng: number } | null | undefined,
  b: { lat: number; lng: number } | null | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lng - b.lng) < 1e-5;
}

function formatListingsLoadError(error: unknown): string {
  const e = error as any;
  const msg = String(e?.message || '');
  const isTimeout =
    e?.code === 'ECONNABORTED' ||
    e?.code === 'ETIMEDOUT' ||
    /timeout/i.test(msg);
  if (isTimeout) {
    return 'The API took too long to respond (database may be slow). Wait a moment and retry.';
  }
  const noResponse = !e?.response && e?.request;
  const st = e?.response?.status;
  const isProxyDown = st === 502 || st === 504;
  const isNetwork =
    isProxyDown ||
    e?.code === 'ECONNREFUSED' ||
    e?.code === 'ERR_NETWORK' ||
    msg === 'Network Error' ||
    noResponse;
  if (isNetwork) {
    return 'Cannot reach the API. From the repo root run npm run server (API on port 5001) while the web app is running. If this persists, check [api] logs for database or port errors.';
  }
  const srvErr = e?.response?.data?.error;
  const details = e?.response?.data?.details;
  const piece = [srvErr, details].filter(Boolean).join(' — ');
  if (st != null) {
    return `Couldn't load listings (HTTP ${st}${piece ? `: ${piece}` : ''}).`;
  }
  return `Failed to load listings: ${msg || 'Please try again.'}`;
}

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const restoreBrowseRef = useRef(
    navigationType === 'POP' ? consumeHomeBrowseState() : null,
  );
  const scrollRestoreDoneRef = useRef(false);

  const initialStored =
    typeof window !== 'undefined' ? loadHomeDeliveryLocation() : null;
  const initialCoords =
    initialStored?.lat != null && initialStored?.lng != null
      ? { lat: initialStored.lat, lng: initialStored.lng }
      : null;
  // Delivery address must not seed city/state filters — that narrows the feed to one
  // city and disables delivery-radius filtering (same as iOS Home).
  const initialFilters = { ...EMPTY_FILTERS };
  const initialCache =
    typeof window !== 'undefined'
      ? readHomeListingsCache(homeFiltersKey(initialFilters), homeCoordsKey(initialCoords))
      : null;
  const skipNextFetchRef = useRef(!!initialCache);

  const [listings, setListings] = useState<Listing[]>(() => initialCache?.listings || []);
  const [offset, setOffset] = useState(() => initialCache?.offset || 0);
  const [hasMore, setHasMore] = useState(() => initialCache?.hasMore ?? true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(() => !initialCache);
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ city?: string; state?: string; streetAddress?: string; streetNumber?: string; streetName?: string } | null>(() => {
    if (!initialStored) return null;
    const label = (initialStored.label || '').trim();
    const streetLine = label ? label.split(',')[0]?.trim() || '' : '';
    if (initialStored.city || initialStored.state || streetLine || initialStored.zip) {
      return {
        city: initialStored.city,
        state: initialStored.state,
        streetAddress: streetLine || initialStored.zip || undefined,
      };
    }
    return null;
  });
  const [filters, setFilters] = useState({ ...initialFilters });
  const [zipCode, setZipCode] = useState(() => initialStored?.zip || '');
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lng: number } | null>(
    () => initialCoords,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({ ...EMPTY_BROWSE_FILTERS });
  const [browseFilterDraft, setBrowseFilterDraft] = useState({ ...EMPTY_BROWSE_DRAFT });
  /** Categories with active listings; scoped to the saved delivery address when set. */
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  /** Listing id -> distance in miles (when user has coordinates and distance filtering ran) */
  const [listingDistances, setListingDistances] = useState<Record<number, number>>(
    () => initialCache?.listingDistances || {},
  );
  const listingsLoadGeneration = useRef(0);
  /** In-range listings left over after filling PAGE_SIZE (so Load more doesn't skip them). */
  const pendingInRangeRef = useRef<Listing[]>(initialCache?.pendingInRange?.slice() || []);
  const pendingDistancesRef = useRef<Record<number, number>>(
    initialCache?.pendingDistances ? { ...initialCache.pendingDistances } : {},
  );
  /** Keep API offset in a ref so Load more never uses a stale closure value. */
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const algoliaPageRef = useRef(0);
  /** Wait for default-address bootstrap before first fetch (avoids show-all then filter flash). */
  const [locationBootstrap, setLocationBootstrap] = useState<'pending' | 'done'>('pending');

  const setCoordinatesIfChanged = useCallback((next: { lat: number; lng: number } | null) => {
    setUserCoordinates((prev) => (coordsNearlyEqual(prev, next) ? prev : next));
  }, []);


  useEffect(() => {
    if (locationBootstrap !== 'done') return;

    let cancelled = false;

    const applyCategoryKeys = (rawKeys: string[]) => {
      const keys = Array.from(
        new Set(rawKeys.map((key) => normalizeListingCategory(key)).filter((key) => key.length > 0)),
      ).sort((a, b) =>
        formatListingCategoryLabel(a).localeCompare(formatListingCategoryLabel(b)),
      );
      if (cancelled) return;
      setExistingCategories(keys);
      setFilters((prev) => {
        if (!prev.category) return prev;
        const selected = normalizeListingCategory(prev.category);
        if (keys.includes(selected)) return prev;
        return { ...prev, category: '' };
      });
    };

    const loadExistingCategories = async () => {
      // No saved delivery address → any category that has active listings.
      if (!userCoordinates) {
        try {
          const rows = await listingsAPI.getTopCategories(0);
          const list = Array.isArray(rows) ? rows : [];
          applyCategoryKeys(list.map((row) => String(row?.category || '')));
        } catch {
          if (!cancelled) setExistingCategories([]);
        }
        return;
      }

      // With a delivery address, only categories that can deliver there.
      try {
        const found = new Set<string>();
        let offset = 0;
        const limit = 100;
        const maxPages = 15;

        for (let page = 0; page < maxPages; page += 1) {
          if (cancelled) return;
          const batch = await listingsAPI.getAll({ limit, offset, light: 1 });
          const rows = Array.isArray(batch) ? batch : [];
          if (rows.length === 0) break;
          offset += rows.length;

          const filtered = await filterListingsByDeliveryRange(rows, userCoordinates, {
            keepUserId: user?.id ?? null,
          });
          for (const listing of filtered.listings) {
            const key = normalizeListingCategory(listing.category);
            if (key) found.add(key);
          }
          if (rows.length < limit) break;
        }

        applyCategoryKeys(Array.from(found));
      } catch {
        if (!cancelled) setExistingCategories([]);
      }
    };

    loadExistingCategories();
    return () => {
      cancelled = true;
    };
  }, [locationBootstrap, userCoordinates, user?.id]);

  // Restore delivery location from storage, then seed from default address only if none saved.
  const applyStoredDeliveryLocation = useCallback(async (isCancelled: () => boolean) => {
    const stored = loadHomeDeliveryLocation();
    if (!stored) return;

    const label = (stored.label || '').trim();
    const streetLine = label ? label.split(',')[0]?.trim() || '' : '';
    if (stored.city || stored.state || streetLine || stored.zip) {
      setUserLocation({
        city: stored.city,
        state: stored.state,
        streetAddress: streetLine || stored.zip || undefined,
      });
    }
    if (stored.zip) setZipCode(stored.zip);
    if (stored.lat != null && stored.lng != null) {
      setCoordinatesIfChanged({ lat: stored.lat, lng: stored.lng });
    }

    // Heal missing IDs in the background — do not block listing load.
    if ((stored.state_id == null || stored.city_id == null) && stored.city && stored.state) {
      findLocationIds(stored.city, stored.state)
        .then((ids) => {
          if (isCancelled()) return;
          if (ids.stateId != null || ids.cityId != null) {
            saveHomeDeliveryLocation({
              city_id: ids.cityId ?? null,
              state_id: ids.stateId ?? null,
              city: stored.city,
              state: stored.state,
              lat: stored.lat,
              lng: stored.lng,
              label: stored.label,
              zip: stored.zip,
            });
          }
        })
        .catch(() => {});
    }
  }, [setCoordinatesIfChanged]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const geocodeDeliverTo = (fullAddress: string) => {
      const address = (fullAddress || '').trim();
      if (!address) return;
      locationsAPI.geocode(address).then((coords) => {
        if (cancelled) return;
        const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
        const lat = c?.lat;
        const lng = c && ('lng' in c ? c.lng : c.lon);
        if (lat != null && lng != null) {
          setCoordinatesIfChanged({ lat, lng });
          saveHomeDeliveryLocation({ lat, lng, fullAddress: address });
        }
      }).catch(() => {});
    };

    (async () => {
      await applyStoredDeliveryLocation(isCancelled);
      if (cancelled) return;

      // Prefer having coords before first fetch so we don't flash "all" then filter down.
      const stored = loadHomeDeliveryLocation();
      if (stored && (stored.lat == null || stored.lng == null)) {
        const healAddress =
          (stored.label || '').trim() ||
          [stored.city, stored.state].filter(Boolean).join(', ') ||
          '';
        if (healAddress && /[a-zA-Z]/.test(healAddress) && !/^\d{5}$/.test(healAddress)) {
          try {
            await Promise.race([
              locationsAPI.geocode(healAddress).then((coords) => {
                if (cancelled) return;
                const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
                const lat = c?.lat;
                const lng = c && ('lng' in c ? c.lng : c.lon);
                if (lat != null && lng != null) {
                  setCoordinatesIfChanged({ lat, lng });
                  saveHomeDeliveryLocation({ lat, lng, fullAddress: healAddress });
                }
              }),
              new Promise((resolve) => setTimeout(resolve, 2500)),
            ]);
          } catch {
            /* continue without coords */
          }
        }
      }

      // Logged-in: only seed from profile default when the buyer has not already
      // chosen a browse delivery location (don't overwrite their selection).
      if (isAuthenticated && !hasUserDeliveryLocation()) {
        try {
          const addrs: any[] = await shippingAddressesAPI.getAll();
          if (cancelled) return;
          const list = Array.isArray(addrs) ? addrs : [];
          const defaultAddr = list.find((a: any) => isDefaultAddress(a));
          if (defaultAddr) {
            const fullAddressStr = (defaultAddr.address || '').trim();
            const city = (defaultAddr.city || '').trim() || parseCityFromAddress(fullAddressStr);
            const stateAbbr = (defaultAddr.state || '').trim() || parseStateFromAddress(fullAddressStr);
            if (city && stateAbbr) {
              const streetLine = (defaultAddr.line1 || '').trim() || parseStreetFromAddress(fullAddressStr);
              const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
              const streetNumber = numMatch ? numMatch[1].trim() : '';
              const streetName = numMatch ? numMatch[2].trim() : streetLine;
              setUserLocation({ city, state: stateAbbr, streetNumber, streetName, streetAddress: streetLine || undefined });
              const fullAddress = fullAddressStr.length > (city.length + stateAbbr.length + 2)
                ? fullAddressStr
                : [streetLine, city, stateAbbr].filter(Boolean).join(', ');

              const ids = await findLocationIds(city, stateAbbr);
              if (cancelled) return;
              saveHomeDeliveryLocation({
                zip: defaultAddr.postal_code,
                city,
                state: stateAbbr,
                fullAddress,
                label: streetLine || fullAddress,
                city_id: ids.cityId ?? null,
                state_id: ids.stateId ?? null,
              });
              geocodeDeliverTo(fullAddress);
            }
          }
        } catch {
          /* ignore */
        }
      }

      if (!cancelled) setLocationBootstrap('done');
    })();

    const onDeliveryUpdated = () => {
      void applyStoredDeliveryLocation(isCancelled);
    };
    window.addEventListener('delivery-location-updated', onDeliveryUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('delivery-location-updated', onDeliveryUpdated);
    };
  }, [isAuthenticated, applyStoredDeliveryLocation, setCoordinatesIfChanged]);

  const refreshDefaultAddress = useCallback(() => {
    if (!isAuthenticated) return;
    // Never overwrite a browse location the user already chose.
    if (hasUserDeliveryLocation()) return;
    shippingAddressesAPI.getAll().then((addrs: any[]) => {
      const list = Array.isArray(addrs) ? addrs : [];
      const defaultAddr = list.find((a: any) => isDefaultAddress(a));
      if (!defaultAddr) return;
      const fullAddressStr = (defaultAddr.address || '').trim();
      const city = (defaultAddr.city || '').trim() || parseCityFromAddress(fullAddressStr);
      const stateAbbr = (defaultAddr.state || '').trim() || parseStateFromAddress(fullAddressStr);
      if (!city || !stateAbbr) return;
      const streetLine = (defaultAddr.line1 || '').trim() || parseStreetFromAddress(fullAddressStr);
      const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
      const streetNumber = numMatch ? numMatch[1].trim() : '';
      const streetName = numMatch ? numMatch[2].trim() : streetLine;
      setUserLocation({ city, state: stateAbbr, streetNumber, streetName, streetAddress: streetLine || undefined });
      const fullAddress = fullAddressStr.length > (city.length + stateAbbr.length + 2)
        ? fullAddressStr
        : [streetLine, city, stateAbbr].filter(Boolean).join(', ');

      findLocationIds(city, stateAbbr).then((ids) => {
        saveHomeDeliveryLocation({
          zip: defaultAddr.postal_code,
          city,
          state: stateAbbr,
          fullAddress,
          label: streetLine || fullAddress,
          city_id: ids.cityId ?? null,
          state_id: ids.stateId ?? null,
        });
        locationsAPI.geocode(fullAddress).then((coords) => {
          const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
          const lat = c?.lat;
          const lng = c && ('lng' in c ? c.lng : c.lon);
          if (lat != null && lng != null) {
            setCoordinatesIfChanged({ lat, lng });
            saveHomeDeliveryLocation({ lat, lng, fullAddress });
          }
        }).catch(() => {});
      }).catch(() => {
        saveHomeDeliveryLocation({
          zip: defaultAddr.postal_code,
          city,
          state: stateAbbr,
          fullAddress,
          label: streetLine || fullAddress,
        });
      });
    }).catch(() => {});
  }, [isAuthenticated, setCoordinatesIfChanged]);

  const detectLocation = async () => {
    setLocationLoading(true);
    try {
      console.log('=== DETECTING LOCATION ===');
      
      // Check if geolocation is available
      if (!navigator.geolocation) {
        toast.error('Location services are not available on this device. Please use the filters to select your location manually.');
        setLocationLoading(false);
        return;
      }
      
      const location = await getCurrentLocation();
      console.log('Location detected:', location);
      setUserLocation({ city: location.city, state: location.state });
      
      // Store user coordinates for distance calculation
      if (location.latitude && location.longitude) {
        setCoordinatesIfChanged({ lat: location.latitude, lng: location.longitude });
      }
      
      if (location.stateAbbreviation) {
        console.log('Looking up location IDs for:', location.city, location.stateAbbreviation);
        const { stateId, cityId } = await findLocationIds(
          location.city || '',
          location.stateAbbreviation
        );
        console.log('Location IDs found:', { stateId, cityId });

        // Save for modal/display; coords drive delivery-range filtering — not city filters.
        saveHomeDeliveryLocation({
          lat: location.latitude,
          lng: location.longitude,
          city: location.city || undefined,
          state: location.stateAbbreviation || location.state || undefined,
          label: [location.city, location.state].filter(Boolean).join(', '),
          city_id: cityId ?? null,
          state_id: stateId ?? null,
        });
      } else {
        console.log('No state abbreviation in location');
        if (location.latitude && location.longitude) {
          saveHomeDeliveryLocation({
            lat: location.latitude,
            lng: location.longitude,
            city: location.city || undefined,
            state: location.state || undefined,
            label: [location.city, location.state].filter(Boolean).join(', '),
          });
        }
      }
    } catch (error: any) {
      console.error('Location detection failed or denied:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      
      // Provide user-friendly error messages
      if (error.code === 1) {
        toast.error('Location permission denied. Please enable location access in your browser settings or use the filters to select your location manually.');
      } else if (error.code === 2) {
        toast.error('Location unavailable. Please check your device location settings or use the filters to select your location manually.');
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again or use the filters to select your location manually.');
      } else {
        toast.error('Unable to detect your location. Please use the filters to select your location manually.');
      }
    } finally {
      setLocationLoading(false);
    }
  };

  // Debounce listings fetch — longer when typing a search query (Algolia).
  // Skip only when filters + coords are unchanged (avoids duplicate loads).
  // Do NOT skip when item_type / category / search / location filters change.
  const lastFetchCoordsRef = useRef<string | null>(null);
  const lastFetchFiltersRef = useRef<string | null>(null);
  useEffect(() => {
    if (locationBootstrap !== 'done') return;
    const coordsKey = homeCoordsKey(userCoordinates);
    const filtersKey = homeFiltersKey(filters);
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      setLoading(false);
      lastFetchCoordsRef.current = coordsKey;
      lastFetchFiltersRef.current = filtersKey;
      return;
    }
    if (
      lastFetchFiltersRef.current === filtersKey &&
      lastFetchCoordsRef.current === coordsKey &&
      listings.length > 0
    ) {
      return;
    }
    const delay = filters.search.trim() ? 300 : 80;
    const t = window.setTimeout(() => {
      lastFetchCoordsRef.current = coordsKey;
      lastFetchFiltersRef.current = filtersKey;
      loadListings(false);
    }, delay);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationBootstrap, filters, userCoordinates]);

  const handleSearchSubmit = () => {
    const term = filters.search.trim();
    if (!term) return;
    const params = new URLSearchParams();
    params.set('q', term);
    const savedZipCode = loadHomeDeliveryLocation()?.zip;
    if (savedZipCode) params.set('zip', savedZipCode);
    navigate(`/search?${params.toString()}`);
  };

  const loadListings = async (append = false) => {
    const loadId = ++listingsLoadGeneration.current;
    if (append) {
      setLoadingMore(true);
      // Load-more must never trigger the one-time scroll-restore path.
      scrollRestoreDoneRef.current = true;
    } else {
      // Keep showing the current grid while refreshing — only blank the page on first load.
      if (listings.length === 0) setLoading(true);
      setLoadError(null);
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      algoliaPageRef.current = 0;
      pendingInRangeRef.current = [];
      pendingDistancesRef.current = {};
    }
    try {
      const searchTerm = filters.search.trim();
      let data: Listing[] = [];
      let apiResponseLength = 0;
      let moreAvailable = false;
      let nextOffset = append ? offsetRef.current : 0;

      if (searchTerm) {
        const collected: Listing[] = [];
        let page = append ? algoliaPageRef.current : 0;
        let nbPages = 1;
        const maxPage = page + 10;

        while (collected.length < PAGE_SIZE && page < nbPages && page < maxPage) {
          const result = await searchAlgoliaListings({
            query: searchTerm,
            category: filters.category || undefined,
            page,
            hitsPerPage: PAGE_SIZE,
          });
          nbPages = result.nbPages;

          for (const hit of result.hits) {
            const id = hit.id ?? parseInt(hit.objectID, 10);
            if (!id || !matchesItemType(hit.category, filters.item_type)) continue;

            const listing = algoliaHitToListing(hit);
            if (!listing) continue;
            collected.push(listing);
            if (collected.length >= PAGE_SIZE) break;
          }

          page += 1;
          if (page >= nbPages) break;
        }

        algoliaPageRef.current = page;
        apiResponseLength = collected.length;
        moreAvailable = page < nbPages;
        data = collected;
      } else {
        const distanceActive = !!userCoordinates;
        let fetchOffset = append ? offsetRef.current : 0;
        const collected: Listing[] = [];
        const pageDistances: Record<number, number> = {};
        // Larger pages when filtering by delivery range so more in-area items surface.
        const fetchLimit = distanceActive ? 100 : PAGE_SIZE;
        // Keep scanning until the grid page is full or the API is exhausted.
        const maxAttempts = distanceActive ? 80 : 1;
        const seenIds = new Set<number>(
          append ? listings.map((l) => Number(l.id)).filter((id) => id > 0) : [],
        );

        // Drain leftovers from the previous page so we don't skip in-range items.
        if (distanceActive) {
          while (collected.length < PAGE_SIZE && pendingInRangeRef.current.length > 0) {
            const next = pendingInRangeRef.current.shift()!;
            const id = Number(next.id);
            if (id > 0 && seenIds.has(id)) continue;
            if (id > 0) seenIds.add(id);
            collected.push(next);
            const d = pendingDistancesRef.current[next.id];
            if (d != null) pageDistances[next.id] = d;
            delete pendingDistancesRef.current[next.id];
          }
        }

        for (
          let attempt = 0;
          attempt < maxAttempts && collected.length < PAGE_SIZE;
          attempt += 1
        ) {
          const params: any = { limit: fetchLimit, offset: fetchOffset, light: 1 };
          if (filters.category) params.category = filters.category;
          if (filters.item_type) params.item_type = filters.item_type;

          const page = await listingsAPI.getAll(params);
          apiResponseLength = Array.isArray(page) ? page.length : 0;
          moreAvailable = apiResponseLength >= fetchLimit;
          fetchOffset += apiResponseLength;

          let pageData = page || [];
          if (distanceActive && pageData.length > 0) {
            const filtered = await filterListingsByDeliveryRange(
              pageData,
              userCoordinates,
              { keepUserId: user?.id ?? null },
            );
            pageData = filtered.listings;
            Object.assign(pageDistances, filtered.distances);
            Object.assign(pendingDistancesRef.current, filtered.distances);
          }

          if (loadId !== listingsLoadGeneration.current) return;

          for (const listing of pageData) {
            const id = Number(listing.id);
            if (id > 0 && seenIds.has(id)) continue;
            if (id > 0) seenIds.add(id);
            if (collected.length < PAGE_SIZE) {
              collected.push(listing);
              delete pendingDistancesRef.current[listing.id];
            } else if (distanceActive) {
              pendingInRangeRef.current.push(listing);
            }
          }

          if (collected.length >= PAGE_SIZE || !moreAvailable) break;
          if (!distanceActive) break;
        }

        data = collected;
        nextOffset = fetchOffset;
        if (distanceActive) {
          moreAvailable = moreAvailable || pendingInRangeRef.current.length > 0;
          setListingDistances((prev) => (append ? { ...prev, ...pageDistances } : pageDistances));
        } else if (!append) {
          setListingDistances({});
        }
      }

      if (loadId !== listingsLoadGeneration.current) return;

      const displayCount = data?.length ?? 0;
      setHasMore(moreAvailable);
      if (searchTerm) {
        const next = append ? offsetRef.current + displayCount : displayCount;
        offsetRef.current = next;
        setOffset(next);
      } else {
        offsetRef.current = nextOffset;
        setOffset(nextOffset);
      }

      // Append only — do not touch window.scroll*. Native scroll anchoring is also
      // disabled on the grid so the viewport stays put while cards are added below.
      if (append) {
        if ((data || []).length === 0) {
          setHasMore(moreAvailable);
        } else {
          setListings((prev) => {
            const existing = new Set(prev.map((l) => Number(l.id)));
            const fresh = (data || []).filter((l) => !existing.has(Number(l.id)));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
        }
      } else {
        setListings(data || []);
      }
    } catch (error) {
      console.error('Error loading listings:', error);
      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any;
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
      }
      if (!append) setLoadError(formatListingsLoadError(error));
    } finally {
      if (loadId === listingsLoadGeneration.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (loadingMore) return;
    scrollRestoreDoneRef.current = true;
    void loadListings(true);
  };

  useEffect(() => {
    return () => {
      saveHomeBrowseState(window.scrollY, listings.length);
      if (listings.length > 0) {
        saveHomeListingsCache({
          listings,
          listingDistances,
          offset,
          hasMore: hasMore || pendingInRangeRef.current.length > 0,
          filtersKey: homeFiltersKey(filters),
          coordsKey: homeCoordsKey(userCoordinates),
          scrollY: window.scrollY,
          pendingInRange: pendingInRangeRef.current.slice(),
          pendingDistances: { ...pendingDistancesRef.current },
        });
      }
    };
  }, [listings, listingDistances, offset, hasMore, filters, userCoordinates]);

  useEffect(() => {
    // One-time scroll restore when returning to Home — never on Load more.
    if (scrollRestoreDoneRef.current) return;
    if (locationBootstrap !== 'done' || loading) return;

    const cachedScroll = initialCache?.scrollY;
    const saved = restoreBrowseRef.current;
    const targetScroll = saved?.scrollY ?? cachedScroll ?? 0;

    if (saved) {
      const needsMoreListings = listings.length < saved.listingsCount && hasMore;
      if (needsMoreListings) {
        if (!loadingMore) void loadListings(true);
        return;
      }
      restoreBrowseRef.current = null;
    }

    scrollRestoreDoneRef.current = true;
    if (targetScroll > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: targetScroll, left: 0, behavior: 'auto' });
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings.length, loading, loadingMore, hasMore, locationBootstrap]);

  const handleZipCodeLookup = async () => {
    if (!zipCode.trim() || !/^\d{5}$/.test(zipCode.trim())) {
      setZipCodeError('Please enter a valid 5-digit zip code');
      return;
    }

    setZipCodeLoading(true);
    setZipCodeError('');

    try {
      const locationData = await locationsAPI.lookupZipCode(zipCode.trim());
      
        if (locationData.state_id) {
          // Set user location for display — do not apply city/state as listing filters
          // (that hides in-range listings from nearby cities).
          setUserLocation({ city: locationData.city_name, state: locationData.state });
          
          // Get coordinates for distance calculation
          let lat = locationData.latitude ?? null;
          let lng = locationData.longitude ?? null;
          if (lat != null && lng != null) {
            setCoordinatesIfChanged({ lat, lng });
          } else if (locationData.city_name && locationData.state) {
            const coords = await geocodeLocation(locationData.city_name, locationData.state);
            if (coords) {
              lat = coords.lat;
              lng = coords.lng;
              setCoordinatesIfChanged({ lat: coords.lat, lng: coords.lng });
            }
          }
          
          saveHomeDeliveryLocation({
            zip: zipCode.trim(),
            city: locationData.city_name,
            state: locationData.state_abbreviation || locationData.state,
            lat,
            lng,
            label: [locationData.city_name, locationData.state].filter(Boolean).join(', '),
            city_id: locationData.city_id ?? null,
            state_id: locationData.state_id ?? null,
          });
          
          setZipCodeError('');
        } else {
          setZipCodeError('Location not found for this zip code');
        }
    } catch (error: any) {
      console.error('Error looking up zip code:', error);
      setZipCodeError(error.response?.data?.error || 'Failed to lookup zip code');
    } finally {
      setZipCodeLoading(false);
    }
  };

  const categories = (() => {
    if (!filters.category) return existingCategories;
    const selected = normalizeListingCategory(filters.category);
    if (existingCategories.includes(selected)) return existingCategories;
    return [...existingCategories, selected].sort((a, b) =>
      formatListingCategoryLabel(a).localeCompare(formatListingCategoryLabel(b)),
    );
  })();

  const activeFilterCount = countHomeBrowseFilters({
    category: filters.category,
    ...browseFilters,
  });

  const displayedListings = useMemo(
    () => applyHomeBrowseFilters(listings, browseFilters),
    [listings, browseFilters],
  );

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    setBrowseFilterDraft({
      minPrice: browseFilters.minPrice,
      maxPrice: browseFilters.maxPrice,
      condition: browseFilters.condition,
      sellerType: browseFilters.sellerType,
      category: filters.category,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot when sheet opens
  }, [mobileFiltersOpen]);

  const applyMobileBrowseFilters = () => {
    setBrowseFilters({
      minPrice: browseFilterDraft.minPrice,
      maxPrice: browseFilterDraft.maxPrice,
      condition: browseFilterDraft.condition,
      sellerType: browseFilterDraft.sellerType,
    });
    setFilters((prev) => ({ ...prev, category: browseFilterDraft.category }));
    setMobileFiltersOpen(false);
  };

  const clearMobileBrowseFilters = () => {
    setBrowseFilters({ ...EMPTY_BROWSE_FILTERS });
    setBrowseFilterDraft({ ...EMPTY_BROWSE_DRAFT });
    setFilters((prev) => ({
      ...prev,
      category: '',
    }));
    setMobileFiltersOpen(false);
  };

  const hasMobileBrowseFilters =
    activeFilterCount > 0 || filters.search.trim() !== '' || filters.item_type !== '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 min-w-0">
        {/* Mobile — one search, location, horizontal category blocks */}
        <div className="sm:hidden mb-4 space-y-2.5 min-w-0">
          <div className="flex min-w-0 items-center gap-2 py-1">
            <div className="relative flex-1 min-w-0">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none"
                aria-hidden
              />
              <input
                type="search"
                enterKeyHint="search"
                placeholder="Search for anything"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                className="w-full min-w-0 box-border rounded-full border border-gray-300 bg-white py-2 pl-9 pr-3 text-base focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((open) => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-label="Filters"
              className={`relative shrink-0 flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                mobileFiltersOpen || activeFilterCount > 0
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600'
              }`}
            >
              <i className="ri-equalizer-line text-lg" aria-hidden />
              {activeFilterCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-white px-0.5 text-[9px] font-bold text-green-700 ring-1 ring-green-600">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="Item type">
            {[
              { id: '', label: 'Everything', icon: 'ri-store-3-line' },
              { id: 'other', label: 'For sale', icon: 'ri-shopping-bag-3-line' },
              { id: 'food', label: 'Food', icon: 'ri-restaurant-line' },
            ].map(({ id, label, icon }) => {
              const active = filters.item_type === id;
              return (
                <button
                  key={id || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilters({ ...filters, item_type: id, category: '' })}
                  className={`flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-3 text-center transition-colors ${
                    active
                      ? 'border-green-500 bg-green-50 text-green-800 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  <i className={`${icon} text-xl leading-none`} aria-hidden />
                  <span className="text-[11px] font-semibold leading-tight px-0.5">{label}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setShowLocationModal(true)}
            className="flex w-full min-w-0 items-center gap-2.5 px-3 py-3.5 text-left"
          >
            <i className="ri-map-pin-2-fill shrink-0 text-base text-green-600 leading-none" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
              <span className="font-medium text-gray-500">Deliver to </span>
              <span className="font-semibold">
                {userLocation ? formatHomeMobileDeliveryLine(userLocation) : 'Set delivery address'}
              </span>
            </span>
            {userLocation ? (
              <span className="shrink-0 text-xs font-semibold text-green-600">Edit</span>
            ) : null}
          </button>

          {mobileFiltersOpen && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 pt-2 space-y-3">
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
                <button
                  type="button"
                  onClick={clearMobileBrowseFilters}
                  className="text-xs font-medium text-green-600"
                >
                  Clear all
                </button>
                <span className="text-sm font-semibold text-gray-900">Filters</span>
                <button
                  type="button"
                  onClick={applyMobileBrowseFilters}
                  className="text-xs font-semibold text-green-600"
                >
                  Apply
                </button>
              </div>
              <HomeBrowseFilterFields
                draft={browseFilterDraft}
                onChange={(patch) =>
                  setBrowseFilterDraft((prev) => ({ ...prev, ...patch }))
                }
                categories={categories}
              />
            </div>
          )}
        </div>

        {/* Item Type Selector — desktop */}
        <div className="hidden sm:block mb-6 sm:mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 overflow-hidden">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-5 min-w-0">
            <h2 className="text-base font-semibold text-gray-900 shrink-0 sm:text-2xl sm:font-bold">
              What are you looking for?
            </h2>

            {/* Delivery address beside title (mobile home address is under category tabs). */}
            {!userLocation ? (
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="flex w-full sm:w-auto items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm font-semibold text-gray-900"
              >
                <i className="ri-map-pin-2-fill text-green-600 text-xl shrink-0 leading-none" aria-hidden />
                <span className="truncate">Where to deliver?</span>
              </button>
            ) : (
              <div className="flex sm:w-auto sm:min-w-[280px] items-center gap-3 min-w-0">
                  <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200">
                    <i className="ri-map-pin-2-fill text-green-600 text-xl shrink-0 leading-none" aria-hidden />
                    <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900 truncate">
                      {formatHomeMobileDeliveryLine(userLocation)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLocationModal(true)}
                    className="shrink-0 px-1 text-sm text-green-600 hover:text-green-700 font-semibold"
                  >
                    Edit
                  </button>
                </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:gap-4 min-w-0">
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: '', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === ''
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-store-3-line text-2xl" aria-hidden />
              <span>Everything</span>
            </button>
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: 'other', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === 'other'
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-shopping-bag-3-line text-2xl" aria-hidden />
              <span>Items for Sale</span>
            </button>
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: 'food', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === 'food'
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-restaurant-line text-2xl" aria-hidden />
              <span className="sm:hidden">Food</span>
              <span className="hidden sm:inline">Homemade Meals & Food</span>
            </button>
          </div>
        </div>

        <div className="mb-4 sm:mb-8 hidden sm:flex items-center justify-between">
          <p className="text-gray-600 text-base">
            {userLocation?.city && userLocation?.state
              ? `Showing items near ${userLocation.city}, ${userLocation.state}`
              : userLocation?.city || userLocation?.state
              ? `Showing items near ${userLocation.city || userLocation.state}`
              : 'Browse local listings'}
          </p>
        </div>

        {/* Filters — desktop (original layout) */}
        <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Filters</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-2 mb-5 sm:mb-6">
            With a <span className="font-medium text-gray-700">delivery address</span> set above, we show listings with{' '}
            <span className="font-medium text-gray-700">delivery available</span> to you when possible.
            Condition and seller type apply to the listings shown below.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4">
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                className="w-full bg-white px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              />
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition
              </label>
              <select
                value={browseFilters.condition}
                onChange={(e) =>
                  setBrowseFilters((prev) => ({ ...prev, condition: e.target.value }))
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              >
                {LISTING_CONDITION_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seller type
              </label>
              <select
                value={browseFilters.sellerType}
                onChange={(e) =>
                  setBrowseFilters((prev) => ({ ...prev, sellerType: e.target.value }))
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              >
                {LISTING_SELLER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {formatListingCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setFilters({ ...EMPTY_FILTERS });
                  setBrowseFilters({ ...EMPTY_BROWSE_FILTERS });
                }}
                className="w-full sm:w-auto min-w-[120px] px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <HomeListingsSkeleton />
        ) : listings.length === 0 || displayedListings.length === 0 ? (
          <div className="text-center py-16 sm:py-20 bg-white rounded-xl shadow-sm border border-gray-200">
            {loadError ? (
              <>
                <i className="ri-error-warning-line text-6xl text-amber-500 mb-4"></i>
                <h3 className="text-lg sm:text-xl text-gray-900 font-semibold mb-2">Couldn&apos;t load listings</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-md mx-auto">{loadError}</p>
                <button
                  type="button"
                  onClick={() => loadListings()}
                  className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium border-0 transition-all shadow-sm"
                >
                  Retry
                </button>
              </>
            ) : listings.length === 0 ? (
              <>
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg sm:text-xl text-gray-900 font-semibold mb-2">No listings found</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  {hasMobileBrowseFilters
                    ? 'Try adjusting your filters or search terms'
                    : 'Be the first to create one!'}
                </p>
                {hasMobileBrowseFilters && (
                  <button
                    type="button"
                    onClick={() => clearMobileBrowseFilters()}
                    className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
                  >
                    Clear all filters
                  </button>
                )}
                {isAuthenticated && !hasMobileBrowseFilters && (
                  <Link
                    to="/sell"
                    className="inline-block px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
                  >
                    Sell in the app
                  </Link>
                )}
              </>
            ) : (
              <>
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg sm:text-xl text-gray-900 font-semibold mb-2">No listings match your filters</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  Try changing price, condition, seller type, or category.
                </p>
                <button
                  type="button"
                  onClick={clearMobileBrowseFilters}
                  className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div
              id="home-listings-grid"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 [overflow-anchor:none]"
            >
              {displayedListings.map((listing, index) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  showOpenStatus={false}
                  showBadges={false}
                  distanceMiles={listingDistances[listing.id]}
                  priority={index < 8}
                />
              ))}
            </div>
            {hasMore && (
              <div
                id="home-load-more"
                className="mt-8 flex justify-center [overflow-anchor:none]"
              >
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  aria-busy={loadingMore}
                  className="inline-flex h-12 min-w-[9.5rem] items-center justify-center gap-2 px-6 bg-green-500 hover:bg-green-600 disabled:opacity-70 text-white font-medium rounded-lg border-0 transition-colors"
                >
                  {loadingMore ? (
                    <>
                      <span
                        className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                        aria-hidden
                      />
                      <span>Loading</span>
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <HomeLocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onAddressesUpdated={refreshDefaultAddress}
        onLocationSelect={async (cityId, stateId, cityName, stateName, fullAddress?: string) => {
          // Delivery address sets coords for range filtering — not city/state listing filters.
          const streetLine = fullAddress ? fullAddress.split(',')[0]?.trim() || '' : '';
          const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
          const streetNumber = numMatch ? numMatch[1].trim() : '';
          const streetName = numMatch ? numMatch[2].trim() : streetLine;
          setUserLocation({ city: cityName, state: stateName, streetAddress: streetLine, streetNumber, streetName });
          const stored = await saveDeliveryLocationFromModal(cityName, stateName, fullAddress, cityId, stateId);
          if (stored?.lat != null && stored?.lng != null) {
            setCoordinatesIfChanged({ lat: stored.lat, lng: stored.lng });
          }
          setShowLocationModal(false);
        }}
        currentCityId={loadHomeDeliveryLocation()?.city_id}
        currentStateId={loadHomeDeliveryLocation()?.state_id}
      />
    </div>
  );
};

export default Home;

